/**
 * MetricsSettingsPanel — popover/sheet for starring and hiding health metrics.
 * Desktop: inline popover (parent positions). Mobile: bottom sheet via portal.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsCompactLayout } from '@/common/hooks';
import { metricOf, providerOf } from './utils/metric-keys';
import { PROVIDER_COLORS } from './constants';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Match a provider-prefixed key against a prefs array that may contain legacy unprefixed keys. */
function prefsMatch(prefs: string[], key: string): boolean {
  if (prefs.includes(key)) return true;
  const m = metricOf(key);
  if (m === key) return false;
  return prefs.includes(m) && !prefs.some(p => p !== m && p.endsWith('/' + m));
}

// ─── Category map ────────────────────────────────────────────────────────────

const METRIC_CATEGORY_MAP: Record<string, string> = {
  // Readiness
  readinessScore: 'Readiness',
  temperatureDeviation: 'Readiness',
  temperatureTrendDeviation: 'Readiness',
  resilienceLevel: 'Readiness',
  // Sleep
  sleepScore: 'Sleep',
  deepSleep: 'Sleep',
  sleepEfficiency: 'Sleep',
  sleepLatency: 'Sleep',
  remSleep: 'Sleep',
  sleepRestfulness: 'Sleep',
  sleepTiming: 'Sleep',
  totalSleep: 'Sleep',
  sleepDuration: 'Sleep',
  sleepAwakeTime: 'Sleep',
  sleepDeepDuration: 'Sleep',
  sleepRemDuration: 'Sleep',
  sleepLightDuration: 'Sleep',
  sleepRestlessPeriods: 'Sleep',
  sleepEfficiencyPct: 'Sleep',
  sleepAverageBreathRate: 'Sleep',
  sleepAverageHRV: 'Sleep',
  sleepAverageHR: 'Sleep',
  sleepLowestHR: 'Sleep',
  minutesAsleep: 'Sleep',
  minutesAwake: 'Sleep',
  // Activity
  activityScore: 'Activity',
  steps: 'Activity',
  activeCalories: 'Activity',
  totalCalories: 'Activity',
  equivalentWalkingDistance: 'Activity',
  highActivityTime: 'Activity',
  mediumActivityTime: 'Activity',
  lowActivityTime: 'Activity',
  sedentaryTime: 'Activity',
  restingTime: 'Activity',
  vo2Max: 'Activity',
  cardioFitnessScore: 'Activity',
  activeMinutes: 'Activity',
  floors: 'Activity',
  distance: 'Activity',
  movingTime: 'Activity',
  elapsedTime: 'Activity',
  totalElevationGain: 'Activity',
  avgSpeed: 'Activity',
  maxSpeed: 'Activity',
  stravaCalories: 'Activity',
  // Heart
  heartRate: 'Heart',
  spo2Average: 'Heart',
  vascularAge: 'Heart',
  restingHeartRate: 'Heart',
  fatBurnMinutes: 'Heart',
  cardioMinutes: 'Heart',
  peakMinutes: 'Heart',
  hrvDailyRmssd: 'Heart',
  avgHeartrate: 'Heart',
  maxHeartrate: 'Heart',
  sessionHeartRate: 'Heart',
  sessionHRV: 'Heart',
  // Stress
  stressHigh: 'Stress',
  recoveryHigh: 'Stress',
  // Body
  weight: 'Body',
  bmi: 'Body',
  bodyFat: 'Body',
  skinTemperature: 'Body',
  breathingRate: 'Body',
  respiratoryRate: 'Sleep',
  // Workout
  workoutDuration: 'Workout',
  workoutIntensity: 'Workout',
  // Whoop (category-level — legacy data)
  'whoop:strain': 'Activity',
  'whoop:recovery': 'Recovery',
  'whoop:sleep': 'Sleep',
  'whoop:workout': 'Workout',
  // Whoop (metric-level — new individual stats)
  strain: 'Activity',
  recoveryScore: 'Recovery',
  hrv: 'Recovery',
  sleepPerformance: 'Sleep',
  sleepConsistency: 'Sleep',
  skinTemp: 'Body',
  spo2: 'Heart',
  workoutStrain: 'Workout',
  workoutCalories: 'Workout',
  workoutAvgHr: 'Workout',
  workoutMaxHr: 'Workout',
  workoutDistance: 'Workout',
  zoneRest: 'Workout',
  zoneFatBurn: 'Workout',
  zoneCardio: 'Workout',
  zonePeak: 'Workout',
  lightSleep: 'Sleep',
  awakeTime: 'Sleep',
  disturbances: 'Sleep',
  maxHeartRateBody: 'Heart',
  // Nutrition
  calories: 'Nutrition',
  protein: 'Nutrition',
  // Vitals
  systolic: 'Vitals',
  diastolic: 'Vitals',
};

const CATEGORY_ORDER = [
  'Readiness', 'Sleep', 'Activity', 'Heart', 'Stress',
  'Body', 'Recovery', 'Workout', 'Nutrition', 'Vitals', 'Other',
];

// ─── Types ───────────────────────────────────────────────────────────────────

interface MetricsSettingsPanelProps {
  metrics: string[];
  starred: string[];
  hidden: string[];
  metricLabels: Record<string, string>;
  onUpdate: (starred: string[], hidden: string[]) => void;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
}

// ─── Icons ───────────────────────────────────────────────────────────────────

const StarIcon = ({ filled, disabled }: { filled: boolean; disabled?: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? '#f59e0b' : 'none'}
    stroke={disabled && !filled ? '#d1d5db' : filled ? '#f59e0b' : '#9ca3af'}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const EyeIcon = ({ open }: { open: boolean }) => open ? (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
) : (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ─── Panel content ───────────────────────────────────────────────────────────

function PanelContent({
  metrics, starred, hidden, metricLabels, onUpdate, onClose,
}: Omit<MetricsSettingsPanelProps, 'anchorRef'>) {
  const [localStarred, setLocalStarred] = useState<string[]>(starred);
  const [localHidden, setLocalHidden] = useState<string[]>(hidden);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');

  // Filter metrics by search, then group by category
  const filteredMetrics = search.trim()
    ? metrics.filter(key => {
        const m = metricOf(key);
        const prov = providerOf(key);
        const label = (metricLabels[m] ?? metricLabels[key] ?? m).toLowerCase();
        const q = search.toLowerCase();
        return label.includes(q) || m.toLowerCase().includes(q) || prov.includes(q);
      })
    : metrics;

  const grouped = filteredMetrics.reduce<Record<string, string[]>>((acc, key) => {
    const m = metricOf(key);
    const cat = METRIC_CATEGORY_MAP[m] ?? METRIC_CATEGORY_MAP[key] ?? 'Other';
    (acc[cat] ??= []).push(key);
    return acc;
  }, {});

  // Sort metrics within each category: starred → visible → hidden
  for (const cat of Object.keys(grouped)) {
    grouped[cat].sort((a, b) => {
      const aStarred = prefsMatch(localStarred, a) ? 0 : 1;
      const bStarred = prefsMatch(localStarred, b) ? 0 : 1;
      if (aStarred !== bStarred) return aStarred - bStarred;
      const aHidden = prefsMatch(localHidden, a) ? 1 : 0;
      const bHidden = prefsMatch(localHidden, b) ? 1 : 0;
      return aHidden - bHidden;
    });
  }

  const categories = CATEGORY_ORDER.filter(c => grouped[c]?.length);

  const toggleStar = (key: string) => {
    if (prefsMatch(localStarred, key)) {
      const next = localStarred.filter(k => k !== key && metricOf(k) !== metricOf(key));
      setLocalStarred(next);
      onUpdate(next, localHidden);
    } else if (localStarred.length < 6) {
      const next = [...localStarred, key];
      setLocalStarred(next);
      onUpdate(next, localHidden);
    }
  };

  const toggleHide = (key: string) => {
    let nextHidden: string[];
    let nextStarred = localStarred;
    if (prefsMatch(localHidden, key)) {
      nextHidden = localHidden.filter(k => k !== key && metricOf(k) !== metricOf(key));
    } else {
      nextHidden = [...localHidden, key];
      // Unstar if hiding
      if (prefsMatch(localStarred, key)) {
        nextStarred = localStarred.filter(k => k !== key && metricOf(k) !== metricOf(key));
        setLocalStarred(nextStarred);
      }
    }
    setLocalHidden(nextHidden);
    onUpdate(nextStarred, nextHidden);
  };

  const toggleCategory = (cat: string) =>
    setCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }));

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px 10px', borderBottom: '1px solid #f3f4f6', flexShrink: 0,
      }}>
        <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--color-text-muted)' }}>Customize Metrics</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '12px', color: 'var(--color-text-faint)', fontWeight: 500 }}>
            {localStarred.length}/6 starred
          </span>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            display: 'flex', alignItems: 'center', borderRadius: 4,
          }}>
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: '8px 16px 4px', flexShrink: 0 }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search metrics..."
          style={{
            width: '100%', padding: '6px 10px', fontSize: '12px',
            border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none',
            background: '#f9fafb', color: 'var(--color-text-muted)',
            boxSizing: 'border-box',
          }}
          onFocus={e => { e.target.style.borderColor = '#9ca3af'; }}
          onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
        />
      </div>

      {/* Scrollable list */}
      <div className="xv-scroll" style={{ overflowY: 'auto', flex: 1, minHeight: 0, padding: '6px 0 8px' }}>
        {/* Starred section — always shown at top */}
        {localStarred.length > 0 && (
          <div>
            <div style={{
              padding: '7px 16px', fontSize: '11px', fontWeight: 600, color: '#f59e0b',
              textTransform: 'uppercase', letterSpacing: '0.06em',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              Starred ({localStarred.length}/6)
            </div>
            {localStarred.map(key => {
              const m = metricOf(key);
              const prov = providerOf(key);
              const label = metricLabels[m] ?? metricLabels[key] ?? m;
              return (
                <div key={`starred-${key}`} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '6px 16px',
                }}>
                  <span style={{ fontSize: '13px', color: 'var(--color-text-muted)', flex: 1, marginRight: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {label}
                    {prov && (
                      <span style={{
                        fontSize: '9px', fontWeight: 600, color: '#fff',
                        background: PROVIDER_COLORS[prov] || '#9ca3af',
                        borderRadius: 4, padding: '1px 5px',
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                        lineHeight: '14px', whiteSpace: 'nowrap',
                      }}>{prov}</span>
                    )}
                  </span>
                  <button
                    onClick={() => toggleStar(key)}
                    title="Unstar"
                    style={{
                      background: 'none', border: 'none', padding: 3,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', borderRadius: 4,
                    }}>
                    <StarIcon filled disabled={false} />
                  </button>
                </div>
              );
            })}
            <div style={{ height: 1, background: '#f3f4f6', margin: '6px 16px' }} />
          </div>
        )}

        {categories.map(cat => (
          <div key={cat}>
            {/* Category header */}
            <button onClick={() => toggleCategory(cat)} style={{
              width: '100%', background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '7px 16px', textAlign: 'left',
            }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {cat}
              </span>
              <ChevronIcon open={!collapsed[cat]} />
            </button>

            {/* Metric rows */}
            {!collapsed[cat] && grouped[cat].map(key => {
              const isStarred = prefsMatch(localStarred, key);
              const isHidden = prefsMatch(localHidden, key);
              const starDisabled = !isStarred && localStarred.length >= 6;
              const m = metricOf(key);
              const prov = providerOf(key);
              const label = metricLabels[m] ?? metricLabels[key] ?? m.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();

              return (
                <div key={key} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '6px 16px', opacity: isHidden ? 0.45 : 1,
                }}>
                  <span style={{ fontSize: '13px', color: 'var(--color-text-muted)', flex: 1, marginRight: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {label}
                    {prov && (
                      <span style={{
                        fontSize: '9px', fontWeight: 600, color: '#fff',
                        background: PROVIDER_COLORS[prov] || '#9ca3af',
                        borderRadius: 4, padding: '1px 5px',
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                        lineHeight: '14px', whiteSpace: 'nowrap',
                      }}>{prov}</span>
                    )}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      onClick={() => !starDisabled && toggleStar(key)}
                      title={starDisabled ? 'Max 6 starred' : isStarred ? 'Unstar' : 'Star'}
                      style={{
                        background: 'none', border: 'none', padding: 3,
                        cursor: starDisabled ? 'default' : 'pointer',
                        display: 'flex', alignItems: 'center', borderRadius: 4,
                        opacity: starDisabled ? 0.4 : 1,
                      }}>
                      <StarIcon filled={isStarred} disabled={starDisabled} />
                    </button>
                    <button
                      onClick={() => toggleHide(key)}
                      title={isHidden ? 'Show metric' : 'Hide metric'}
                      style={{
                        background: 'none', border: 'none', padding: 3,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', borderRadius: 4,
                      }}>
                      <EyeIcon open={!isHidden} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────

export function MetricsSettingsPanel(props: MetricsSettingsPanelProps) {
  const { onClose, anchorRef, ...rest } = props;
  const isCompact = useIsCompactLayout();
  const panelRef = useRef<HTMLDivElement>(null);

  // Click-outside to close (desktop only)
  useEffect(() => {
    if (isCompact) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current && !panelRef.current.contains(target) &&
          (!anchorRef?.current || !anchorRef.current.contains(target))) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isCompact, onClose, anchorRef]);

  if (isCompact) {
    // Bottom sheet via portal
    return createPortal(
      <div style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}>
        {/* Backdrop */}
        <div onClick={onClose} style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)',
        }} />
        {/* Sheet */}
        <div ref={panelRef} style={{
          position: 'relative', zIndex: 1,
          background: 'var(--color-surface)',
          borderRadius: '16px 16px 0 0',
          maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
          animation: 'slideUp 0.22s ease',
        }}>
          {/* Drag handle */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 2px' }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: '#e5e7eb' }} />
          </div>
          <PanelContent {...rest} onClose={onClose} />
        </div>
        <style>{`@keyframes slideUp { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
      </div>,
      document.body,
    );
  }

  // Desktop inline popover
  return (
    <div ref={panelRef} style={{
      background: 'var(--color-surface)',
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      boxShadow: 'var(--popover-shadow, 0 8px 24px rgba(0,0,0,0.10))',
      width: 280,
      maxHeight: 420,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <PanelContent {...rest} onClose={onClose} />
    </div>
  );
}

export default MetricsSettingsPanel;
